// src/store/slices/notificationsSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  readAt?: string;
  createdAt: string;
}

interface NotificationsState {
  items: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.items = action.payload;
      state.unreadCount = action.payload.filter(n => !n.read).length;
    },

    addNotification: (state, action: PayloadAction<Notification>) => {
      state.items.unshift(action.payload);
      if (!action.payload.read) {
        state.unreadCount += 1;
      }
    },

    markAsRead: (state, action: PayloadAction<{ id: string; readAt?: string }>) => {
      const notification = state.items.find(n => n.id === action.payload.id);
      if (notification && !notification.read) {
        notification.read = true;
        // FIX: Usar condicional para atribuir readAt
        if (action.payload.readAt !== undefined) {
          notification.readAt = action.payload.readAt;
        }
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    markAllAsRead: (state) => {
      const now = new Date().toISOString();
      state.items.forEach(notification => {
        if (!notification.read) {
          notification.read = true;
          notification.readAt = now;
        }
      });
      state.unreadCount = 0;
    },

    deleteNotification: (state, action: PayloadAction<string>) => {
      const index = state.items.findIndex(n => n.id === action.payload);
      if (index !== -1) {
        const notification = state.items[index];
        if (!notification.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.items.splice(index, 1);
      }
    },

    clearAll: (state) => {
      state.items = [];
      state.unreadCount = 0;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setNotifications,
  addNotification,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  clearAll,
  setLoading,
  setError,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
